import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "assets/images/logo.svg";
import users from "assets/images/sidebar/users.svg";
import users2 from "assets/images/sidebar/users2.svg";
import hotDesk from "assets/images/sidebar/hotDesk.png";
import hotDesk2 from "assets/images/sidebar/hotDesk2.png";
import office from "assets/images/sidebar/office.png";
import office2 from "assets/images/sidebar/office2.png";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  hasMenuPermission,
  hasPermission,
} from "components/constants/Permissions";
import { setIsPermissionChecked } from "store/reducers/authReducer";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getNewWorkshopRequestCount } from "store/actions/workspaceSlice";
import {
  AmenityIcon,
  APILogsIcon,
  BranchesIcon,
  CityIcon,
  CouponIcon,
  CustomerIcon,
  DemoRequestIcon,
  HotDeskIcon,
  MeetingRoomIcon,
  OfficeIcon,
  OrderIcon,
  OrganizationIcon,
  OverviewIcon,
  PlanAndPricingIcon,
  ReviewIcon,
  RewardIcons,
  SettingsIcon,
  UsersIcon,
  WebsiteRequestIcon,
  WorkshopIcon,
  WorkspaceIcon,
  WSManagementIcon,
} from "components/common/SVGIcons";

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isSubMenuOpen, setIsSubMenuOpen] = useState({
    reviews: false,
    workshops: false,
    rewards: false,
  });

  const { isGettingUserDetails, adminPermissions, accountType } = useSelector(
    (state) => state.auth
  );

  const { newWorkshopRequestCount = null } = useSelector(
    (state) => state.Workspace || {}
  );

  // Function to handle toggle for sub-menu
  const toggleSubMenu = (menu) => {
    setIsSubMenuOpen((prevState) => ({
      ...prevState,
      [menu]: !prevState[menu],
    }));
  };

  // Function to add 'active' class to the active link
  const isActive = (paths) =>
    paths.includes(location.pathname) ? "active" : "";

  // ACTIVE or OPEN SUB LINKS
  useEffect(() => {
    const path = location.pathname;
    // Automatically set submenu state based on the current path
    setIsSubMenuOpen({
      reviews: path.includes("/dashboards/reviews"),
      workshops: path.includes("/bookings/workshops"),
      rewards: path.includes("/rewards"),
    });
  }, [location.pathname]);

  useEffect(() => {
    dispatch(setIsPermissionChecked(true)); // Start permission checking
    dispatch(getNewWorkshopRequestCount());
    const currentPath = location.pathname;

    // Map paths to permission IDs
    const permissionMap = {
      "/dashboards/overview": "1_1",
      "/dashboards/reviews": "1_2",
      "/dashboards/reviews/review-history": "1_3",
      "/bookings/bookings": "2_1",
      "/bookings/workspace": "2_2",
      "/bookings/meeting-rooms": "2_3",
      "/bookings/workshops": "2_4",
      "/bookings/website-requests": "2_5",
      "/bookings/offices": "2_6",
      "/bookings/hot-desks": "2_7",
      "/management/cities": "3_1",
      "/management/amenities": "3_2",
      "/management/branches": "3_3",
      "/users": "5_2",
    };

    const currentPermissionId = permissionMap[currentPath];

    const checkPermissions = async () => {
      if (
        !isGettingUserDetails && // Ensure user details are loaded
        currentPermissionId &&
        !hasPermission(accountType, adminPermissions, currentPermissionId)
      ) {
        // Find the first accessible route
        const findFirstAccessibleRoute = () => {
          for (const group of adminPermissions) {
            for (const child of group.children) {
              if (child.permissions.isView) {
                const route = Object.keys(permissionMap).find(
                  (key) => permissionMap[key] === child.id
                );
                return route;
              }
            }
          }
          dispatch();
          return "/"; // Default redirect if no accessible routes
        };

        const redirectTo = findFirstAccessibleRoute();
        if (redirectTo) navigate(redirectTo, { replace: true });
      }

      // Simulate permission check delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      dispatch(setIsPermissionChecked(false)); // Stop checking after permission verification
    };

    checkPermissions();
  }, [
    location.pathname,
    accountType,
    adminPermissions,
    navigate,
    isGettingUserDetails,
    dispatch,
  ]); // Ensure dependencies are correct

  return (
    <div className="sidebar_menu">
      <div className="sidebar_logo">
        <Link to={"/dashboards/overview"}>
          <img src={Logo} alt="Ashjar Space Logo" width={100} height={43} />
        </Link>
      </div>
      <nav id="sidebar-menu">
        <ul>
          {hasMenuPermission(accountType, adminPermissions, 1) && (
            <li className="menu-title">{t("Dashboards")} </li>
          )}
          {hasPermission(accountType, adminPermissions, "1_1") && (
            <li>
              <Link
                to="/dashboards/overview"
                className={isActive(["/dashboards/overview"])}
              >
                <OverviewIcon />
                {t("Overview")}
              </Link>
            </li>
          )}
          {hasPermission(accountType, adminPermissions, "1_2") && (
            <li
              className={`menu-item`}
              onClick={() => toggleSubMenu("reviews")}
            >
              <Link
                to="/dashboards/reviews"
                className={`p_r ${isActive(["/dashboards/reviews"])}`}
              >
                <ChevronRight
                  className={`arrow_right_icon ${
                    isSubMenuOpen.reviews ? "active" : ""
                  }`}
                />
                <ReviewIcon />
                {t("Reviews")}
              </Link>
              <ul className={`sub-menu ${isSubMenuOpen.reviews ? "open" : ""}`}>
                {hasPermission(accountType, adminPermissions, "1_3") && (
                  <li>
                    <Link
                      to="/dashboards/reviews/review-history"
                      className={isActive([
                        "/dashboards/reviews/review-history",
                      ])}
                    >
                      {t("Reviews History")}
                    </Link>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* Spaces */}
          {hasMenuPermission(accountType, adminPermissions, 2) && (
            <li className="menu-title">{t("Organization")}</li>
          )}

          {hasPermission(accountType, adminPermissions, "3_1") && (
            <li>
              <Link
                to="/management/organisation"
                className={isActive([
                  "/management/organisation",
                  "/organisation-details",
                  "/management/organisation/invite-organisation",
                  "/organisation/organisation-details",
                ])}
              >
                <OrganizationIcon />
                {t("Organization")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_1") && (
            <li>
              <Link
                to="/management/subscriptions"
                className={isActive([
                  "/management/subscriptions",
                  "/management/subscriptions/subscription-details",
                ])}
              >
                <OrderIcon />
                {t("Subscriptions")}
              </Link>
            </li>
          )}
          {hasPermission(accountType, adminPermissions, "3_1") && (
            <li>
              <Link
                to="/organization/demo-request"
                className={isActive(["/organization/demo-request"])}
              >
                <DemoRequestIcon />
                {t("Demo Request")}
              </Link>
            </li>
          )}

          {/* Spaces */}
          {hasMenuPermission(accountType, adminPermissions, 2) && (
            <li className="menu-title">{t("Spaces")}</li>
          )}

          {hasPermission(accountType, adminPermissions, "2_2") && (
            <li>
              <Link
                to="/bookings/workspace"
                className={isActive([
                  "/bookings/workspace",
                  "/bookings/workspace/add-new",
                  "/bookings/workspace/workspace-details",
                  "/bookings/workspace/edit-workspace",
                ])}
              >
                <WorkspaceIcon />
                {t("Workspace")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_3") && (
            <li>
              <Link
                to="/bookings/meeting-rooms"
                className={isActive([
                  "/bookings/meeting-rooms",
                  "/bookings/meeting-rooms/meeting-room-details",
                  "/bookings/meeting-rooms/update-meeting-room",
                  "/bookings/meeting-rooms/add-new",
                ])}
              >
                <MeetingRoomIcon />
                {t("Meeting Rooms")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_6") && (
            <li>
              <Link
                to="/bookings/offices"
                className={isActive([
                  "/bookings/offices",
                  "/bookings/offices/office-details",
                  "/bookings/offices/update-office",
                  "/bookings/offices/add-new",
                ])}
              >
                <OfficeIcon />
                {/* <img
                  src={office}
                  className="main_icon"
                  alt="Icon"
                  width={20}
                  height={20}
                />
                <img
                  src={office2}
                  className="hover_icon"
                  alt="Icon"
                  width={20}
                  height={20}
                /> */}
                {t("Offices")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_7") && (
            <li>
              <Link
                to="/bookings/hot-desks"
                className={isActive([
                  "/bookings/hot-desks",
                  "/bookings/hot-desks/update-hot-desk",
                  "/bookings/hot-desks/add-new",
                  "/bookings/hot-desk/hot-desk-details",
                ])}
              >
                <HotDeskIcon />
                {t("Hot Desks")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_4") && (
            <li
              className={`menu-item`}
              onClick={() => toggleSubMenu("workshops")}
            >
              <Link
                to="/bookings/workshops"
                className={`p_r ${isActive([
                  "/bookings/workshops",
                  "/bookings/workshops/workshop-details",
                  "/bookings/workshops/update-workshop",
                ])}`}
              >
                <ChevronRight
                  className={`arrow_right_icon ${
                    isSubMenuOpen.workshops ? "active" : ""
                  }`}
                />
                <WorkshopIcon />
                {t("Workshops")}
                {newWorkshopRequestCount > 0 && (
                  <span className="notification-badge">
                    {newWorkshopRequestCount}
                  </span>
                )}
              </Link>
              <ul
                className={`sub-menu ${isSubMenuOpen.workshops ? "open" : ""}`}
              >
                <li>
                  <Link
                    to="/bookings/workshops/add-new"
                    className={isActive([
                      "/bookings/workshops/add-new" ||
                        "/bookings/workshops/update-workshop",
                    ])}
                  >
                    {t("Add New")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/bookings/workshops/workshops-requests"
                    className={isActive([
                      "/bookings/workshops/workshops-requests",
                    ])}
                  >
                    {t("Requests")}
                    {newWorkshopRequestCount > 0 && (
                      <span className="notification-badge-2">
                        {newWorkshopRequestCount}
                      </span>
                    )}
                  </Link>
                </li>
              </ul>
            </li>
          )}

          {/* BOOKINGS */}
          {hasMenuPermission(accountType, adminPermissions, 2) && (
            <li className="menu-title">{t("Bookings")}</li>
          )}

          {hasPermission(accountType, adminPermissions, "2_1") && (
            <li>
              <Link
                to="/bookings/bookings"
                className={isActive([
                  "/bookings/bookings",
                  "/bookings/bookings/booking-details",
                ])}
              >
                <OrderIcon />

                {t("Bookings")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "5_1") && (
            <li>
              <Link
                className={isActive([
                  "/customers",
                  "/customers/customer-information",
                ])}
                to="/customers"
              >
                <CustomerIcon />

                {t("Customers")}
              </Link>
            </li>
          )}

          {hasMenuPermission(accountType, adminPermissions, 3) && (
            <li className="menu-title">{t("Management")}</li>
          )}

          {hasPermission(accountType, adminPermissions, "3_1") && (
            <li>
              <Link
                to="/management/cities"
                className={isActive(["/management/cities"])}
              >
                <CityIcon />
                {t("Cities")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_2") && (
            <li>
              <Link
                to="/management/amenities"
                className={isActive(["/management/amenities"])}
              >
                <AmenityIcon />
                {t("Amenities")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_3") && (
            <li>
              <Link
                to="/management/branches"
                className={isActive([
                  "/management/branches",
                  "/management/branches/add-branch",
                  "/management/branches/branch-details",
                  "/management/branches/update-branch",
                ])}
              >
                <BranchesIcon />

                {t("Branches")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_4") && (
            <li>
              <Link
                to="/management/ws-management"
                className={isActive([
                  "/management/ws-management",
                  "/management/ws-management/add-new-closure",
                  "/management/ws-management/update-closure",
                  "/management/ws-management/closure-details",
                ])}
              >
                <WSManagementIcon />
                {t("WS Management")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_5") && (
            <li>
              <Link
                to="/management/settings"
                className={isActive([
                  "/management/settings",
                  "/management/settings/notifications",
                  "/management/settings/privacy-and-security",
                  "/management/settings/introduction-page",
                  "/management/settings/global-settings",
                ])}
              >
                <SettingsIcon />

                {t("Settings")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_6") && (
            <li>
              <Link
                to="/management/api-logs"
                className={isActive([
                  "/management/api-logs",
                  "/management/api-logs/details",
                ])}
              >
                <APILogsIcon />

                {t("API Logs")}
              </Link>
            </li>
          )}

          {/* {hasPermission(accountType, adminPermissions, "2_5") && (
            <li>
              <Link
                to="/bookings/website-requests"
                className={isActive(["/bookings/website-requests"])}
              >
                <WebsiteRequestIcon />
                {t("Website Requests")}
              </Link>
            </li>
          )} */}
          {hasPermission(accountType, adminPermissions, "2_5") && (
            <li>
              <Link
                to="/management/plans"
                className={isActive([
                  "/management/plans",
                  "/management/plans/edit-plan",
                ])}
              >
                <PlanAndPricingIcon />
                {t("Plans & pricing")}
              </Link>
            </li>
          )}

          {/* Rewards Submenu */}

          {hasMenuPermission(accountType, adminPermissions, 4) && (
            <li className="menu-title">{t("Rewards")}</li>
          )}

          {hasMenuPermission(accountType, adminPermissions, 4) && (
            <li className="menu-item" onClick={() => toggleSubMenu("rewards")}>
              <Link
                to="/rewards/rewards"
                className={`p_r ${isActive([
                  "/rewards/rewards",
                  "/rewards/cashback",
                  "/rewards/coupon",
                  "/rewards/rewards-page",
                ])} `}
              >
                <ChevronRight
                  className={`arrow_right_icon ${
                    isSubMenuOpen.rewards ? "active" : ""
                  }`}
                />
                <RewardIcons />
                {t("Rewards")}
              </Link>
              <ul className={`sub-menu ${isSubMenuOpen.rewards ? "open" : ""}`}>
                <li>
                  <Link
                    to="/rewards/cashback"
                    className={isActive(["/rewards/cashback"])}
                  >
                    {t("Cashback")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/rewards/referral"
                    className={isActive(["/rewards/referral"])}
                  >
                    {t("Referral Code")}
                  </Link>
                </li>
              </ul>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "4_2") && (
            <li>
              <Link
                to="/rewards/coupons"
                className={isActive([
                  "/rewards/coupons",
                  "/rewards/coupons/add-new-coupon",
                  "/rewards/coupons/update-coupon",
                ])}
              >
                <CouponIcon />

                {t("Coupons")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "4_3") && (
            <li>
              <Link
                to="/plan/offers"
                className={isActive([
                  "/plan/offers",
                  "/plan/offers/add-new-offer",
                  "/plan/offers/update-offer",
                ])}
              >
                <CouponIcon />

                {t("Plan Offers")}
              </Link>
            </li>
          )}

          {/* CUSTOMERS & USERS */}
          {hasMenuPermission(accountType, adminPermissions, 5) && (
            <li className="menu-title">{t("Users")}</li>
          )}

          {accountType === "superadmin" && (
            <li>
              <Link className={isActive(["/roles"])} to="/roles">
                <UsersIcon />
                {t("Roles")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "5_2") && (
            <li>
              <Link
                className={isActive(["/users", "/users/user-details"])}
                to="/users"
              >
                <UsersIcon />
                {t("Users")}
              </Link>
            </li>
          )}
        </ul>
        <div className="footer_logo">
          <img src={Logo} alt="logo" width={80} height={35} />
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
